import React, { useState } from 'react';

interface Message {
  id: number;
  text: string;
  sender: 'user' | 'donna';
  timestamp: Date;
}

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: "Hi! I'm Donna, Alberto's personal assistant. Ask me anything about Alberto!",
      sender: 'donna',
      timestamp: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // Sample responses - TODO: Replace with actual AI integration (OpenAI, etc.)
  const getResponse = (question: string): string => {
    const lowerQuestion = question.toLowerCase();

    if (lowerQuestion.includes('experience') || lowerQuestion.includes('work')) {
      return "Alberto is currently pursuing his MBA at MIT Sloan, focusing on Product Management and Entrepreneurship. Previously, he worked as a Cloud Deployed Engineer at AWS, delivering cloud projects for Fortune 500 companies. He has experience in DevOps, Infrastructure, MLOps, and AI.";
    }

    if (lowerQuestion.includes('education') || lowerQuestion.includes('study') || lowerQuestion.includes('degree')) {
      return "Alberto holds a Double Master's degree in Industrial Technologies & Smart Industry from Comillas University in Madrid, where he graduated in the top 3% of his class. He's currently pursuing his MBA at MIT Sloan School of Management.";
    }

    if (lowerQuestion.includes('skill') || lowerQuestion.includes('technology') || lowerQuestion.includes('tech stack')) {
      return "Alberto's technical skills include: Cloud Computing (AWS, GCP), Programming (Python, TypeScript, JavaScript), Data & AI (Machine Learning, TensorFlow), DevOps (IaC, Containerization), and databases (SQL, NoSQL). He's also proficient in product management and agile methodologies.";
    }

    if (lowerQuestion.includes('language')) {
      return "Alberto speaks four languages: Spanish (native), English (fluent), German (advanced), and French (intermediate).";
    }

    if (lowerQuestion.includes('project')) {
      return "Alberto has worked on various projects including WallSpot (advertising platform), Eddy (referral/loyalty system), face recognition systems, AWS cloud architectures, and supply chain optimization projects that saved over $1M at Amazon.";
    }

    if (lowerQuestion.includes('aws') || lowerQuestion.includes('amazon')) {
      return "At AWS, Alberto delivered 6 cloud projects for Fortune 500 companies, published 4 AWS blog posts, created 1 official AWS Architecture, holds 6 AWS certifications, and became the worldwide AWS problem-solving champion in 2023. He also mentored 3 junior consultants. You can view his certifications on Credly: credly.com/users/alberto-menendez.ac26ae8f";
    }

    if (lowerQuestion.includes('certification') || lowerQuestion.includes('credly') || lowerQuestion.includes('certificate')) {
      return "Alberto has 6 AWS certifications. You can view them all on his Credly profile: https://www.credly.com/users/alberto-menendez.ac26ae8f";
    }

    if (lowerQuestion.includes('podcast') || lowerQuestion.includes('migration') || lowerQuestion.includes('mainframe')) {
      return "Alberto has appeared on AWS podcasts discussing cloud migrations! He was a guest on Charlas Técnicas de AWS (episode on migrating 1000+ applications to the cloud) and Podcast AWS LATAM (EP103 on mainframe migrations at scale). Check out the Podcasts section for links!";
    }

    if (lowerQuestion.includes('contact') || lowerQuestion.includes('reach') || lowerQuestion.includes('email')) {
      return "You can reach Alberto at albemene@mit.edu or call (617) 388-4716. He's also active on LinkedIn!";
    }

    if (lowerQuestion.includes('location') || lowerQuestion.includes('where')) {
      return "Alberto is currently based in Cambridge, MA, while pursuing his MBA at MIT Sloan.";
    }

    if (lowerQuestion.includes('hobby') || lowerQuestion.includes('hobbies') || lowerQuestion.includes('interest')) {
      return "Alberto enjoys padel, sailing, chess, soccer, reading about neuroscience, and traveling!";
    }

    if (lowerQuestion.includes('hello') || lowerQuestion.includes('hi') || lowerQuestion.includes('hey')) {
      return "Hello! How can I help you learn more about Alberto today?";
    }

    if (lowerQuestion.includes('thank')) {
      return "You're welcome! Is there anything else you'd like to know about Alberto?";
    }

    return "That's a great question! I'd recommend checking out the different sections of Alberto's portfolio for more detailed information, or feel free to reach out directly at albemene@mit.edu.";
  };

  const handleSendMessage = () => {
    if (!inputText.trim()) return;

    // Add user message
    const userMessage: Message = {
      id: messages.length + 1,
      text: inputText,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages([...messages, userMessage]);
    setInputText('');
    setIsTyping(true);

    // Simulate typing delay
    setTimeout(() => {
      const response = getResponse(inputText);
      const donnaMessage: Message = {
        id: messages.length + 2,
        text: response,
        sender: 'donna',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, donnaMessage]);
      setIsTyping(false);
    }, 1000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chatbot Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 shadow-lg transition transform hover:scale-110 flex items-center justify-center group"
          aria-label="Open chat with Donna"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
            1
          </div>
          <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block">
            <div className="bg-gray-900 text-white text-sm rounded-lg px-3 py-2 whitespace-nowrap">
              Chat with Donna
            </div>
          </div>
        </button>
      )}

      {/* Chatbot Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-2xl">
                  👩‍💼
                </div>
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
              </div>
              <div>
                <h3 className="font-semibold">Donna</h3>
                <p className="text-xs text-blue-100">Alberto's Assistant</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 rounded-full p-2 transition"
              aria-label="Close chat"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 max-h-96">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.sender === 'user'
                      ? 'bg-blue-600 text-white rounded-br-none'
                      : 'bg-white text-gray-800 shadow-md rounded-bl-none'
                  }`}
                >
                  <p className="text-sm">{message.text}</p>
                  <p className={`text-xs mt-1 ${message.sender === 'user' ? 'text-blue-100' : 'text-gray-400'}`}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl rounded-bl-none px-4 py-3 shadow-md">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-4 bg-white">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me about Alberto..."
                className="flex-1 border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputText.trim()}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-full p-2 transition"
                aria-label="Send message"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              💡 Try asking about experience, skills, or projects!
            </p>
          </div>
        </div>
      )}
    </>
  );
}
